import { t } from 'i18next';
import { useMutation, useQuery } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';
import { GetOneResult, IInfiniteQueryOptions, IRequestPayload } from 'core/types';
import { elementService } from 'elements/services';
import {
  IComponentTemplate,
  IComponentTemplateRequestPayload,
  IElementTemplate,
  IUpsertComponentTemplatePayload,
  IUpsertElementTemplatePayload,
} from 'elements/types';

export const useGetElementTemplates = (options: IInfiniteQueryOptions<IElementTemplate> = {}) =>
  useInfiniteQuery<IElementTemplate>(
    elementService.getElementTemplates,
    'elementTemplates',
    {},
    {
      ...options,
      fetchAllPages: true,
    }
  );

export const useGetElementTemplate = (
  criteria: IRequestPayload,
  enabled = true
): GetOneResult<IElementTemplate> => {
  const { data, isLoading, isError } = useQuery(
    ['elementTemplate', criteria],
    ({ signal }) => elementService.getElementTemplate(criteria, signal),
    { enabled }
  );

  return {
    record: data?.record ?? null,
    isLoading,
    isError,
  };
};

export const useGetComponentTemplates = (
  requestPayload: IComponentTemplateRequestPayload = {},
  options: IInfiniteQueryOptions<IComponentTemplate> = {}
) =>
  useInfiniteQuery<IComponentTemplate>(
    elementService.getComponentTemplates,
    'componentTemplates',
    requestPayload,
    {
      ...options,
      fetchAllPages: true,
    }
  );

interface IElementTemplateActions {
  position?: NotificationPosition;
  selectionCount?: number;
}

export const useElementTemplateActions = ({
  selectionCount,
  position,
}: IElementTemplateActions = {}) => ({
  ...useUpsertElementTemplate(),
  ...useAddElementTemplateToJob(),
  ...useRemoveElementTemplates(selectionCount, position),
});

const useUpsertElementTemplate = () => {
  const { errors } = useHandleErrors({
    success: ({ elementTemplate }: IUpsertElementTemplatePayload) =>
      elementTemplate.id
        ? { message: t('messageElementTemplateUpdateSuccess') }
        : { message: t('messageElementTemplateCreateSuccess') },
    loading: ({ elementTemplate }: IUpsertElementTemplatePayload) =>
      elementTemplate.id
        ? { message: t('messageElementTemplateUpdateInProgress') }
        : { message: t('messageElementTemplateCreateInProgress') },
    error: ({ elementTemplate }: IUpsertElementTemplatePayload) =>
      elementTemplate.id
        ? { title: t('errorTitleElementTemplateUpdate') }
        : { title: t('errorTitleElementTemplateCreate') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(elementService.upsertElementTemplate, { ...errors });

  return { upsertElementTemplate: mutateAsync };
};

const useAddElementTemplateToJob = () => {
  const { errors } = useHandleErrors({
    success: () => ({ message: t('messageElementTemplateAddToJobSuccess') }),
    loading: () => ({ message: t('messageElementTemplateAddToJobInProgress') }),
    error: () => ({ title: t('errorTitleElementTemplateAddToJob') }),
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(elementService.addElementTemplateToJob, { ...errors });

  return { addElementTemplateToJob: mutateAsync };
};

const useRemoveElementTemplates = (
  selectionCount?: number,
  position: NotificationPosition = NotificationPosition.TOP
) => {
  const { errors } = useHandleErrors({
    success: () => ({
      message: t('messageElementTemplatesRemoveSuccess', { count: selectionCount }),
    }),
    loading: () => ({
      message: t('messageElementTemplatesRemoveInProgress', { count: selectionCount }),
    }),
    error: () => ({ title: t('errorTitleElementTemplatesRemove', { count: selectionCount }) }),
    position,
  });

  const { mutateAsync } = useMutation(
    (data: { ids: number[] }) => elementService.removeElementTemplates(data),
    { ...errors }
  );

  return { removeElementTemplates: mutateAsync };
};

interface IComponentTemplateActions {
  selectionCount?: number;
}

export const useComponentTemplateActions = ({
  selectionCount,
}: IComponentTemplateActions = {}) => ({
  ...useUpsertComponentTemplate(),
  ...useUpsertComponentTemplateAssociation(),
  ...useValidateComponentTemplatePart(),
  ...useRemoveComponentTemplateAssociations(selectionCount),
});

const useUpsertComponentTemplate = () => {
  const { errors } = useHandleErrors({
    success: ({ componentTemplate }: IUpsertComponentTemplatePayload) =>
      componentTemplate.id
        ? { message: t('messageComponentTemplateUpdateSuccess') }
        : { message: t('messageComponentTemplateCreateSuccess') },
    loading: ({ componentTemplate }: IUpsertComponentTemplatePayload) =>
      componentTemplate.id
        ? { message: t('messageComponentTemplateUpdateInProgress') }
        : { message: t('messageComponentTemplateCreateInProgress') },
    error: ({ componentTemplate }: IUpsertComponentTemplatePayload) =>
      componentTemplate.id
        ? { title: t('errorTitleComponentTemplateUpdate') }
        : { title: t('errorTitleComponentTemplateCreate') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(elementService.upsertComponentTemplate, { ...errors });

  return { upsertComponentTemplate: mutateAsync };
};

const useUpsertComponentTemplateAssociation = () => {
  const { errors } = useHandleErrors({
    success: () => ({ message: t('messageComponentTemplateUpdateSuccess') }),
    loading: () => ({ message: t('messageComponentTemplateUpdateInProgress') }),
    error: () => ({ title: t('errorTitleComponentTemplateUpdate') }),
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation(elementService.upsertComponentTemplateAssociation, {
    ...errors,
  });

  return { upsertComponentTemplateAssociation: mutateAsync };
};

const useValidateComponentTemplatePart = () => {
  const { errors } = useHandleErrors({
    loading: () => ({ message: t('messageComponentTemplateValidateInProgress') }),
    error: () => ({ title: t('errorTitleComponentTemplateValidate') }),
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(elementService.validateComponentTemplatePart, {
    ...errors,
  });

  return { validateComponentTemplatePart: mutateAsync };
};

const useRemoveComponentTemplateAssociations = (selectionCount: number) => {
  const { errors } = useHandleErrors({
    success: { message: t('messageComponentTemplatesRemoveSuccess', { count: selectionCount }) },
    loading: { message: t('messageComponentTemplatesRemoveInProgress', { count: selectionCount }) },
    error: { title: t('errorTitleComponentTemplatesRemove', { count: selectionCount }) },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation(
    (data: { ids: number[] }) => elementService.removeComponentTemplateAssociations(data),
    { ...errors }
  );

  return { removeComponentTemplateAssociations: mutateAsync };
};
